/** @jsx jsx */

import type {
  HTMLAttributes,
  HTMLProps,
  InputHTMLAttributes,
  MouseEventHandler,
  ReactNode,
} from 'react';
import { forwardRef, memo } from 'react';
import type { ButtonProps } from '@balance-web/button';
import { jsx } from '@balance-web/core';
import { CheckIcon } from '@balance-web/icon/icons/CheckIcon';
import { XIcon } from '@balance-web/icon/icons/XIcon';
import { SearchIcon } from '@balance-web/icon/icons/SearchIcon';
import { LoadingDots } from '@balance-web/loading';
import { theme } from '@balance-web/theme';
import { MenuChip } from '@balance-web/chip';

const SIZE_KEY = 'base';

// Buttons
// ------------------------------

type SelectMenuButtonProps = {
  hasValue: boolean;
  selectionCount: number;
  onClear?: MouseEventHandler;
} & Omit<ButtonProps, 'variat' | 'size'> & {
    variant: 'filled' | 'outline';
    size: 'small' | 'medium';
  };
export const SelectMenuButton = forwardRef<
  HTMLButtonElement,
  SelectMenuButtonProps
>(({ hasValue, onClear, disabled, selectionCount, ...props }, ref) => {
  // @ts-ignore data-expanded is acceptable internal API
  const isExpanded = props['aria-expanded'] || props['data-expanded'];

  // The absolute relative/positioning is to avoid invalid DOM nesting, where
  // a button is the descendent of another button.
  return (
    <div
      css={{
        alignItems: 'center',
        display: 'inline-flex',
        position: 'relative',
      }}
    >
      <MenuChip
        ref={ref}
        aria-expanded={isExpanded}
        disabled={disabled}
        hasValue={hasValue}
        selectionCount={selectionCount}
        onClear={
          onClear
            ? () => {
                onClear({} as any);
              }
            : undefined
        }
        {...props}
      />
    </div>
  );
});

SelectMenuButton.displayName = 'SelectMenuButton';

export const ClearButton = forwardRef<
  HTMLButtonElement,
  Omit<HTMLProps<HTMLButtonElement>, 'type'>
>(({ children, ...props }, ref) => {
  return (
    <button
      ref={ref}
      aria-label="Clear all values"
      css={{
        alignItems: 'center',
        background: 0,
        border: 0,
        borderRadius: theme.radii.xsmall,
        color: theme.palette.actionButton.text,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        marginRight: theme.spacing.xsmall,
        padding: 0,
        height: theme.sizing.xsmall,
        width: theme.sizing.xsmall,

        '&[disabled]': {
          cursor: 'default',
        },

        ':not(&[disabled])': {
          ':hover, &:focus-visible': {
            background: theme.palette.actionButton.backgroundFocused,
            color: theme.palette.actionButton.textFocused,
            outline: 0,
          },
          ':active': {
            background: theme.palette.actionButton.backgroundPressed,
            color: theme.palette.actionButton.textPressed,
          },
        },
      }}
      type="button"
      {...props}
    >
      <XIcon size="small" />
    </button>
  );
});

ClearButton.displayName = 'ClearButton';

// Menu Provider
// ------------------------------

type MenuProps = {
  children: ReactNode;
  menuWidth?: number;
};

export const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ menuWidth, ...props }, ref) => {
    return (
      <div
        ref={ref}
        css={{
          position: 'relative',
          maxHeight: `calc(${theme.sizing[SIZE_KEY]} * 6.5)`, // show six and half items, creating affordance that there's more to scroll
          minWidth: 220,
          maxWidth: menuWidth ?? 360,
          outline: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',

          ':empty': {
            padding: 0,
          },
        }}
        {...props}
      />
    );
  }
);

Menu.displayName = 'Menu';

// Menu Item
// ------------------------------

type MenuItemProps = {
  /** Item content */
  children: ReactNode;
  /** An event handler for when the item is clicked */
  onClick?: MouseEventHandler;
  /** The role of the item */
  role?: 'menuitemradio' | 'menuitemcheckbox' | 'option';
  isSelected?: boolean;
  // Not used, we pass the aria-disabled attribute through
  isItemDisabled?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const MenuItem = memo(
  forwardRef<HTMLDivElement, MenuItemProps>((props, ref) => {
    const {
      children,
      isSelected,
      /**
       * Not a valid html attribute, this is already account for
       *  by the aria-selected attribute.
       */
      isItemDisabled: _isItemDisabled,
      'aria-disabled': ariaDisabled,
      'aria-selected': ariaSelected,
      ...consumerProps
    } = props;

    // NOTE: must be a button element so click events extend to "Enter/Space" keypress
    return (
      <div
        ref={ref}
        css={{
          alignItems: 'center',
          background: 0,
          border: 0,
          color: theme.palette.menuItem.text,
          cursor: 'pointer',
          display: 'flex',
          fontSize: theme.typography.fontSize.small,
          fontWeight: theme.typography.fontWeight.medium,
          outline: 0, // clicked items will be "real focused". we don't want to show the outline
          paddingBottom: 0,
          paddingTop: 0,
          position: 'relative',
          textAlign: 'left',
        }}
        // tabIndex={0}
        {...consumerProps}
      >
        <div
          aria-disabled={ariaDisabled}
          aria-selected={ariaSelected}
          css={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            minWidth: 0, // resolve flex issues with text trucation
            paddingLeft: theme.spacing.medium,
            paddingRight: theme.spacing.medium,

            // NOTE: the `style` prop applies a pixel height to the parent,
            // necessary for virtualization.
            height: '100%',

            ':active': {
              backgroundColor: theme.palette.menuItem.backgroundPressed,
            },

            // the highlighted styles for hover and faux-focus
            '&[data-balance-component-selected]': {
              backgroundColor: theme.palette.menuItem.backgroundSelected,
            },

            '&[aria-selected=true]': {
              background: theme.palette.menuItem.backgroundFocused,
            },

            // the currently selected item(s)
            '&[aria-checked=true]': {
              color: theme.palette.text.active,
            },

            '&[aria-disabled=true]': {
              color: theme.palette.text.dim,
              pointerEvents: 'none',
            },
          }}
        >
          <SelectionIndicator isSelected={isSelected === true} />
          <div
            css={{
              flex: 1,
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    );
  })
);

MenuItem.displayName = 'MenuItem';

const ICON_SIZE = 16;

// Selection Indicator
// ------------------------------

type IndicatorProps = {
  isSelected?: boolean;
};
const SelectionIndicator = ({ isSelected }: IndicatorProps) => {
  let selectedColor = theme.palette.text.link;
  let gutter = theme.spacing.small;

  // always render the wrapper so siblings don't move around when items are
  // selected vs not-selected. instead, we toggle the icon color for show/hide
  return (
    <span css={{ flexShrink: 0, marginRight: gutter }}>
      <span css={{ color: isSelected ? selectedColor : 'transparent' }}>
        <CheckIcon size={ICON_SIZE} />
      </span>
    </span>
  );
};

// Combo box
// ------------------------------

export const ComboBox = forwardRef<
  HTMLDivElement,
  { showDivider: boolean } & HTMLAttributes<HTMLDivElement>
>(({ children, showDivider, ...props }, ref) => {
  return (
    <div ref={ref} {...props}>
      {children}
      {showDivider && (
        <div
          css={{
            height: 1,
            width: '100%',
            backgroundColor: theme.palette.global.border,
          }}
        />
      )}
    </div>
  );
});

ComboBox.displayName = 'ComboBox';

// Search Input
// ------------------------------

export const SearchInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ disabled, ...props }, ref) => {
  return (
    <div css={{ alignItems: 'center', display: 'flex', position: 'relative' }}>
      <span
        css={{
          display: 'block',
          left: theme.spacing.medium,
          lineHeight: 1,
          marginTop: -1,
          pointerEvents: 'none',
          position: 'absolute',
        }}
      >
        <SearchIcon color="dim" size={ICON_SIZE} />
      </span>
      <input
        ref={ref}
        css={{
          background: 0,
          border: 0,
          boxSizing: 'border-box',
          color: theme.palette.formInput.text,
          fontWeight: theme.typography.fontWeight.medium,
          outline: 0,
          padding: theme.spacing.medium,
          paddingLeft: `calc(${theme.spacing.medium} + ${theme.spacing.small} + ${ICON_SIZE}px)`,
          paddingRight: theme.spacing.large,
          fontSize: theme.typography.fontSize.small,
          width: '100%',

          '&::placeholder': {
            color: theme.palette.formInput.textPlaceholder,
          },
        }}
        tabIndex={disabled ? -1 : 0} // don't actually disable the input, that makes it impossible to focus, even programmatically
        {...props}
      />
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

// Empty State
// ------------------------------

export const EmptyState = (props: { children?: ReactNode }) => {
  return (
    <div
      css={{
        alignItems: 'center',
        color: theme.palette.text.dim,
        display: 'flex',
        flexDirection: 'column',
        fontSize: theme.typography.fontSize.small,
        fontWeight: theme.typography.fontWeight.medium,
        height: theme.sizing[SIZE_KEY],
        justifyContent: 'center',
      }}
      {...props}
    />
  );
};

// Loading Indicator
// ------------------------------

export const LoadingIndicator = () => {
  return (
    <div
      css={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: theme.sizing[SIZE_KEY],
        justifyContent: 'center',
      }}
    >
      <LoadingDots label="Filtering options" />
    </div>
  );
};

/**
 * Component to fill the space where items would typically live within the menu.
 * Used for things like "no results" messages etc.
 */
export const MenuItemSlot = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      css={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
      {...props}
    />
  );
};

/**
 * Component shown at the bottom of a paginated list, revealed when more items
 * are loading.
 */
export const MenuItemLoadingIndicator = (
  props: HTMLAttributes<HTMLDivElement>
) => {
  return (
    <MenuItemSlot {...props}>
      <LoadingDots color="dim" label="loading more" />
    </MenuItemSlot>
  );
};
